// noinspection JSValidateTypes

import _ from 'lodash';
import React, { Component } from 'react';
import { render } from 'react-dom';
import io from'socket.io-client';
import { BrowserRouter, Route, Link, Switch, Redirect } from 'react-router-dom';
import feathers from '@feathersjs/feathers';
import socketio from '@feathersjs/socketio-client';
import auth from '@feathersjs/authentication-client';

import CrawlingViewer from "./pages/CrawlingViewer";
import CrawlingWikiViewer from "./pages/CrawlingWikiViewer";
import CeroKmFragmentsManager from "./pages/cerokm/CeroKmFragmentsManager";
import DataFeedbackManager from './pages/DataFeedbackManager';
import DataIssuesManager from './pages/DataIssuesManager';
import StableUrlsManager from './pages/StableUrlsManager';
import DataProcessDefinitionManager from './pages/DataProcessDefinitionManager';
import DataProcessManager from './pages/DataProcessManager';
import DataTaskBatchWork from "./pages/DataTaskBatchWork";
import DataTasksBatchesManager from "./pages/DataTasksBatchesManager";
import DataTasksBatchesStatistics from "./pages/DataTasksBatchesStatistics";
import DataTasksManager from './pages/DataTasksManager';
import DataViewsManager from './pages/DataViewsManager';
import DeletedLegosManager from './pages/DeletedLegosManager';
import EmailLogViewer from "./pages/EmailLogViewer";
import EmailVariationsViewer from "./pages/EmailVariationsViewer";
import FacetsManager from "./pages/FacetsManager";
import FuseboxManager from './pages/freelancers/FuseboxManager';
import ImgClustersManager from './pages/ImgClustersManager';
import LegoArticlesManager from "./pages/freelancers/LegoArticlesManager";
import LegoDefinitionsManager from "./pages/freelancers/LegoDefinitionsManager";
import LegoSpecsManager from "./pages/freelancers/LegoSpecsManager";
import LegosManager from './pages/LegosManager';
import LinguisticComparison from './pages/LinguisticComparison';
import LoginPage from './pages/LoginPage';
import ManualsBatchUpload from "./pages/ManualsBatchUpload";
import ManualsManager from "./pages/ManualsManager";
import WiringDiagramManager from "./pages/WiringDiagramManager";
import ManualsViewer from "./pages/ManualsViewer";
import PageNotFound from "./pages/PageNotFound";
import RegisteredUserStatsViewer from "./pages/RegisteredUserStatsViewer";
import ScriptsManager from './pages/ScriptsManager';
import SitePagesQualitativeSample from './pages/SitePagesQualitativeSample';
import StatsManager from './pages/StatsManager';
import SymptomsManager from './pages/SymptomsManager';
import TaggedReportsExplorer from './pages/TaggedReportsExplorer';
import TaggedSemanticsExplorer from "./pages/TaggedSemanticsExplorer";
import TestFuseboxEditor from './pages/TestFuseboxEditor';
import TestFuseboxCalibration from './pages/TestFuseboxCalibration';
import TestPage from './pages/TestPage';
import TestPerceptualHash from './pages/TestPerceptualHash';
import TestEmbeddingPage from './pages/TestEmbeddingPage';
import TestImageEmbedSimilarity from './pages/TestFuseboxImageEmbedSimilarity';
import FuseboxDatasetReviewPage from './pages/FuseboxDatasetReviewPage';
import FuseboxEmbeddingBenchmarkPage from './pages/FuseboxEmbeddingBenchmarkPage';
import FuseboxClusterLinksPage from './pages/FuseboxClusterLinksPage';
import SecurityTrafficAnalysis from './pages/SecurityTrafficAnalysis';
import TrimsManager from './pages/TrimsManager';
import TrimsViewer from './pages/subpages/TrimsViewer';
import UserConversationsViewer from './pages/UserConversationsViewer';
import UserTrimsViewer from "./pages/UserTrimsViewer";
import LegosOtherManager from './pages/LegosOtherManager';
import GenerationsManager from './pages/GenerationsManager';
import GenerationsByCountryManager from './pages/GenerationsByCountryManager';
import ModelMiniaturesManager from './pages/ModelMiniaturesManager';

import favicon from '../admin-front/public/icon-lego-worker.png?url';
import ContributionsManager from './pages/ContributionsManager';
import ModelsManager from './pages/ModelsManager';
import CeroKmSchemaManager from './pages/cerokm/CeroKmSchemaManager';
import CeroKmFactoidsManager from './pages/cerokm/CeroKmFactoidsManager';
import CeroKmPromptsManager from './pages/cerokm/CeroKmPromptsManager';
import CeroKmSpecsManager from './pages/cerokm/CeroKmSpecsManager';
import CeroKmSourcesManager from './pages/cerokm/CeroKmSourcesManager';
import CeroKmSpecsGroupsManager from './pages/cerokm/CeroKmSpecsGroupsManager';
import CeroKmMissingSpecsManager from './pages/cerokm/CeroKmMissingSpecsManager';

const { protocol, hostname, port } = document.location;

// Set the tab favicon. We must replace the <link> node rather than mutate its
// href: Chromium reads the favicon at parse/first-paint and ignores later
// in-place href changes, so the tab icon would never update.
function setFavicon(href) {
  const oldLink = document.querySelector("link[rel~='icon']");
  if (oldLink && oldLink.href.endsWith(href)) return;
  if (oldLink) oldLink.remove();
  const newLink = document.createElement('link');
  newLink.rel = 'shortcut icon';
  newLink.type = 'image/x-icon';
  newLink.href = href;
  document.head.appendChild(newLink);
}

if (port) {
  // Local development
  window.serverBaseUrl = `${protocol}//${hostname}:3003`;
  // Use development icon
  setFavicon(favicon);
} else {
  // Production
  window.serverBaseUrl = document.location.origin;
}

// This will work either with http://localhost or https://kb.opinautos.com
window.socket = io(window.serverBaseUrl, {
  transports: ['websocket'],
  forceNew: true,
  // Start the first reconnect attempt in ~0.1-0.3s instead of the default 0.5-1.5s
  reconnectionDelay: 200
});

// Chrome freezes background tabs (all but the few most recently used, or CPU-hungry ones under Energy Saver),
// and freezing fails every open WebSocket ("Page entered Back-Forward Cache"), so going back to an older
// admin tab always meant reconnecting. Chrome never freezes a page holding a Web Lock: hold one for the
// page's lifetime, with a per-tab name so tabs don't wait on each other.
navigator.locks?.request(`legos-admin-tab-${Date.now()}-${Math.random()}`, () => new Promise(() => {}));
window.client = feathers();

client.configure(socketio(socket, {
  timeout: 3*60000
}));

window.client.configure(auth({}));

window.initialSocketStatus = 'connecting';
socket.on('connect', () => window.initialSocketStatus = 'connected');

// A hidden tab can come back with a dead socket the client won't notice until its ping times out
// (up to pingInterval + pingTimeout, see admin-server/app.js). Probe it on return: no pong in 3s →
// close the transport so socket.io's regular reconnect (and the auth client's re-authenticate) runs now.
document.addEventListener('visibilitychange', () => {
  const engine = socket.io.engine;
  // A WebSocket the browser already closed (back-forward cache, frozen tab) is reconnecting on its own
  const wsOpen = engine?.transport?.ws?.readyState === WebSocket.OPEN;
  if (document.visibilityState !== 'visible' || !socket.connected || !wsOpen) {
    return;
  }
  const onPong = () => clearTimeout(timer);
  const timer = setTimeout(() => {
    engine.off('pong', onPong);
    engine.close();
  }, 3000);
  engine.once('pong', onPong);
  engine.ping();
});

///////////////////////////////////////////////////////////////////

const sharedRoutes = [
  // Legos
  <Route key='legos' exact path='/legos' render={() => <Redirect to="/legos/fusebox"/>}/>,
  <Route key='legos-specs' exact path='/legos/specs' component={LegoSpecsManager}/>,
  <Route key='legos-fusebox' exact path='/legos/fusebox' component={FuseboxManager}/>,
  <Route key='legos-data-issues' exact path='/legos/data-issues' component={DataIssuesManager}/>,
  <Route key='legos-image-clusters' exact path='/legos/image-clusters' component={ImgClustersManager}/>,
  <Route key='legos-data-feedback' exact path='/legos/data-feedback' component={DataFeedbackManager}/>,
  <Route key='legos-other' exact path='/legos/other' component={LegosOtherManager}/>,
  <Route key='legos-wiring-diagrams' exact path='/legos/wiring-diagrams' component={WiringDiagramManager}/>,

  // Tasks
  <Route key='tasks' exact path='/tasks' render={() => <Redirect to="/tasks/batches"/>}/>,
  <Route key='tasks-batches' exact path='/tasks/batches' component={DataTasksBatchesManager}/>,
  <Route key='tasks-batch' exact path='/tasks/batches/:batchId' component={DataTaskBatchWork}/>,
  <Route key='tasks-contributors' exact path='/tasks/contributors' component={DataTasksBatchesStatistics}/>,

  // Manuals (pages only, redirects differ per role)
  <Route key='manuals-viewer' exact path='/manuals/viewer' component={ManualsViewer}/>,
  <Route key='manuals-batch-upload' exact path='/manuals/batch-upload' component={ManualsBatchUpload}/>,
  <Route key='manuals-edit' exact path='/manuals/edit' component={ManualsManager}/>,

  // Models
  <Route key='models' exact path='/models' render={() => <Redirect to="/models/all"/>}/>,
  <Route key='models-all' exact path='/models/all' component={ModelsManager}/>,
  <Route key='models-gens' exact path='/models/gens' component={GenerationsManager}/>,
  <Route key='models-genbycountry' exact path='/models/genbycountry' component={GenerationsByCountryManager}/>,
  <Route key='models-miniatures' exact path='/models/miniatures' component={ModelMiniaturesManager}/>,

  // Crawl
  <Route key='crawl' exact path='/crawl' render={() => <Redirect to="/crawl/cars"/>}/>,
  <Route key='crawl-cars' exact path='/crawl/cars' component={CrawlingViewer}/>,
  <Route key='crawl-wikiboxes' exact path='/crawl/wikiboxes' component={CrawlingWikiViewer}/>,
  <Route key='crawl-usertrims' exact path='/crawl/usertrims' component={UserTrimsViewer}/>,

  // Emails
  <Route key='emails-conversations' exact path='/emails/conversations' component={UserConversationsViewer}/>,
];

class App extends Component {
  render() {
    let {user, config} = this.props;

    // TODO: Fast hack. The correct way is creating a context that the page can consume
    window.config = _.extend(window.config || {}, config);
    // window.document.title = config.legoAdminTitle;

    // Use the locale-flag favicon matching the admin's site locale (es/en/pt).
    // Other locales keep the default favicon set in index.html.
    // In local development we keep the dev icon set above (see `if (port)`).
    const LOCALE_FAVICONS = { es: '/favicon.es.ico', en: '/favicon.en.ico', pt: '/favicon.pt.ico' };
    const localeFavicon = !document.location.port && LOCALE_FAVICONS[config?.locale];
    // const localeFavicon = '/favicon.pt.ico';
    if (localeFavicon) {
      setFavicon(localeFavicon);
    }

    if(user.isAdmin) {
      return <Switch>
        <Route exact path='/' render={() => <Redirect to="/legos"/>}/>

        <Route exact path='/test' render={() => <Redirect to="/test/fusebox"/>}/>
        <Route exact path='/test/fusebox' component={TestPage}/>
        <Route exact path='/test/perceptualhash' component={TestPerceptualHash}/>
        <Route exact path='/test/embedding' component={TestEmbeddingPage}/>
        <Route exact path='/test/embed-similarity' component={TestImageEmbedSimilarity}/>
        <Route exact path='/test/fusebox-dataset-review' component={FuseboxDatasetReviewPage}/>
        <Route exact path='/test/fusebox-embedding-benchmark' component={FuseboxEmbeddingBenchmarkPage}/>
        <Route exact path='/test/fusebox-clusters-links' component={FuseboxClusterLinksPage}/>

        <Route exact path='/legos/all' component={LegosManager}/>
        <Route exact path='/legos/deleted' component={DeletedLegosManager}/>
        <Route exact path='/legos/articles' component={LegoArticlesManager}/>
        <Route exact path='/legos/definitions' component={LegoDefinitionsManager}/>
        <Route exact path='/legos/contributions' component={ContributionsManager}/>
        <Route exact path='/legos/stable-urls' component={StableUrlsManager}/>

        {sharedRoutes}

        <Route exact path='/dataviews' component={DataViewsManager}/>

        <Route exact path='/stats' render={() => <Redirect to="/stats/fuseboxes"/>}/>
        <Route exact path='/stats/:category' component={StatsManager}/>

        <Route exact path='/trims' component={TrimsManager}/>
        <Route exact path='/trims/viewer' component={TrimsViewer}/>

        <Route exact path='/tasks/workflows' component={DataProcessDefinitionManager}/>
        <Route exact path='/tasks/processes' component={DataProcessManager}/>
        <Route exact path='/tasks/list' component={DataTasksManager}/>
        <Route exact path='/tasks/pages-sampling' component={SitePagesQualitativeSample}/>

        <Route exact path='/fusebox' component={TestFuseboxEditor}/>
        <Route exact path='/fusebox-calibration' component={TestFuseboxCalibration}/>

        <Route exact path='/manuals' render={() => <Redirect to="/manuals/edit"/>}/>

        <Route exact path='/facets' component={FacetsManager}/>

        <Route exact path='/emails' render={() => <Redirect to="/emails/logs"/>}/>
        <Route exact path='/emails/logs' component={EmailLogViewer}/>
        <Route exact path='/emails/variations' component={EmailVariationsViewer}/>

        <Route exact path='/tagging' render={() => <Redirect to="/tagging/explore"/>}/>
        <Route exact path='/tagging/explore' component={TaggedReportsExplorer}/>
        <Route exact path='/tagging/symptoms' render={() => <Redirect to="/tagging/symptoms/aire-acondicionado"/>}/>
        <Route exact path='/tagging/symptoms/:name' component={SymptomsManager}/>
        <Route exact path='/tagging/linguistics' component={TaggedSemanticsExplorer}/>
        <Route exact path='/tagging/linguistic-comparison' component={LinguisticComparison}/>

        <Route exact path='/scripts' component={ScriptsManager}/>

        <Route exact path='/security' render={() => <Redirect to="/security/traffic"/>}/>
        <Route exact path='/security/traffic' component={SecurityTrafficAnalysis}/>

        <Route exact path='/cerokm' render={() => <Redirect to="/cerokm/fragments"/>}/>
        <Route exact path='/cerokm/sources' component={CeroKmSourcesManager}/>
        <Route exact path='/cerokm/fragments' component={CeroKmFragmentsManager}/>
        <Route exact path='/cerokm/factoids' component={CeroKmFactoidsManager}/>
        <Route exact path='/cerokm/schema' component={CeroKmSchemaManager}/>
        <Route exact path='/cerokm/prompts' component={CeroKmPromptsManager}/>
        <Route exact path='/cerokm/specs' component={CeroKmSpecsManager}/>
        <Route exact path='/cerokm/specgroups' component={CeroKmSpecsGroupsManager}/>
        <Route exact path='/cerokm/add-missing-specs' component={CeroKmMissingSpecsManager}/>

        <Route exact path='/users' component={RegisteredUserStatsViewer}/>

        <Route path='*' exact={true} component={PageNotFound}/>
      </Switch>;
    } else if(user.isContributor || user.level === 'bot_contributor') {
      // Bot contributors (API accounts) get the contributors' screens when they log in here
      return <Switch>
        <Route exact path='/' render={() => <Redirect to="/legos"/>}/>        

        {sharedRoutes}

        <Route exact path='/manuals' render={() => <Redirect to="/manuals/viewer"/>}/>

        <Route exact path='/emails' render={() => <Redirect to="/emails/conversations"/>}/>

        <Route path='*' exact={true} component={PageNotFound}/>
      </Switch>
    }
  }
}

let rootElement = document.getElementById('root');

function renderPage(user, config) {
  render((<BrowserRouter>
    <App user={user} config={config}/>
  </BrowserRouter>), rootElement);
}

function loginAndRender() {
// Authenticate with the local email/password strategy
  function onLogin(user, config) {
    try {
      window.user = JSON.parse(localStorage['user']);
      renderPage(user, config);
    } catch (e) {
      render((
        <div className={'alert alert-danger m-3'}>
          There was a problem loading admin page:
          <br/>
          <br/>
          <pre>{e.toString() + e.stack}</pre>
        </div>
      ), rootElement);
    }
  }

  render(<LoginPage key={'initial'} initialEmail={localStorage['lastAccessEmail']} onLogin={onLogin}/>, rootElement);
}

loginAndRender();

